import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { ButtonT1 } from "../../components/atoms/Button";
import { Card as DesignSystemCard } from "../../components/atoms/card";
import { FormInput } from "../../components/atoms/Input";
import { StatusTag } from "../../components/atoms/Tag";
import { PortalHeader } from "../../components/molecules/Header";
import {
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
} from "../../components/organisms/activityLog/index.styles";
import { useDetailPageContext } from "../../components/organisms/Provider/DetailV2";
import { useTaskMonitor } from "../../components/organisms/Provider/TaskProvider";
import { usePlaceTimezone } from "../../helper/usePlaceTimezone";
import { useNotification } from "../../hooks/useNotification";
import { usePermission } from "../../hooks/usePermissions";
import {
  useCreateOceanTrackingMutation,
  useGetOceanTrackingHistoryQuery,
  useGetOceanTrackingIntegrationsQuery,
  useGetOceanTrackingQuery,
  useOceanTrackingActionMutation,
  useUpdateOceanTrackingMutation,
} from "../../store/apiSlice/oceanTrackingApiSlice";
import { apiError, label, reportedTime, trackWhen, validateOceanReference } from "./utils";

/**
 * True while this browser tab is in the foreground.
 *
 * Track polling uses this so a hidden sales-shipment or CSGN-0042 tab does
 * not keep asking Orderlink every 30 seconds.
 *
 * @return {boolean}
 */
function usePageVisible() {
  const [visible, setVisible] = useState(typeof document === "undefined" ? true : !document.hidden);
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
}

const SUCCESSFUL_TASK_STATUSES = new Set(["COMPLETED", "SUCCESS"]);
const TERMINAL_TASK_STATUSES = new Set([
  ...SUCCESSFUL_TASK_STATUSES,
  "DISMISSED",
  "FAILED",
  "FAILURE",
  "REVOKED",
]);

/**
 * How often this Track view should ask Orderlink again.
 *
 * Hidden tabs stay quiet. A first sync or Request refresh on sales
 * shipment #5001 polls every 5 seconds until that job finishes. A
 * settled In transit card does not keep asking every 30 seconds.
 *
 * @param {boolean} pageVisible
 * @param {boolean} inFlight
 * @return {number}
 */
function trackingPollInterval(pageVisible, inFlight) {
  if (!pageVisible || !inFlight) return 0;
  return 5000;
}

/**
 * True while Track is still waiting on a background job.
 *
 * First sync of a new parcel (no data yet) counts. A Request refresh
 * this session counts until Task Manager says SUCCESS or FAILED. A
 * leftover task id from last week does not keep polling.
 *
 * @param {{
 *   waitingForFirstSync: boolean,
 *   startedTaskId: ?string,
 *   taskStatus: ?string
 * }} args
 * @return {boolean}
 */
function trackingWorkInFlight({ waitingForFirstSync, startedTaskId, taskStatus }) {
  if (waitingForFirstSync) return true;
  if (!startedTaskId) return false;
  if (!taskStatus) return true;
  return !TERMINAL_TASK_STATUSES.has(taskStatus);
}

/**
 * Words for a parcel field the courier did not send.
 *
 * On #5001, Ship24 sent scans at Roissy-en-France but no origin. The
 * card should say the courier omitted origin, not that tracking failed.
 *
 * @param {?string} value
 * @param {string} emptyLabel
 * @return {string}
 */
function reportedOr(value, emptyLabel) {
  const text = String(value || "").trim();
  return text || emptyLabel;
}

/**
 * Clock at the end of a scan sentence, if the courier sent one.
 *
 * @param {?string} description
 * @return {string}
 */
function clockFromDescription(description) {
  const match = String(description || "").match(/(\d{2}):(\d{2})\s*$/);
  return match ? `${match[1]}:${match[2]}` : "";
}

/**
 * Instant used to pick the latest scan. Mixed offsets compare as time,
 * not as ISO text.
 *
 * @param {{
 *   id?: number,
 *   event_date?: string,
 *   description?: string,
 *   transport?: {occurred_at?: string}
 * }} event
 * @return {number}
 */
function eventSortInstant(event) {
  const occurred = String(event?.transport?.occurred_at || "").trim();
  if (occurred) {
    const ms = Date.parse(occurred);
    if (!Number.isNaN(ms)) return ms;
  }
  const day = String(event?.event_date || "");
  const clock = clockFromDescription(event?.description);
  if (day && clock) {
    const ms = Date.parse(`${day}T${clock}:00Z`);
    if (!Number.isNaN(ms)) return ms;
  }
  if (day) {
    const ms = Date.parse(`${day}T00:00:00Z`);
    if (!Number.isNaN(ms)) return ms;
  }
  return Number(event?.id) || 0;
}

/**
 * Place from the latest scan, not the oldest one on the same day.
 *
 * On #5002 the notebook snapshot can still say Data Center while
 * Detailed events already show a failed attempt in Paris.
 *
 * @param {Array<{
 *   id?: number,
 *   event_date?: string,
 *   description?: string,
 *   location?: {name?: string},
 *   transport?: {occurred_at?: string}
 * }>} events
 * @return {string}
 */
function latestOccurredLocation(events) {
  if (!events?.length) return "";
  let best = "";
  let bestInstant = Number.NEGATIVE_INFINITY;
  let bestId = Number.NEGATIVE_INFINITY;
  for (const event of events) {
    const name = String(event.location?.name || "").trim();
    if (!name) continue;
    const instant = eventSortInstant(event);
    const id = Number(event?.id) || 0;
    if (instant > bestInstant || (instant === bestInstant && id > bestId)) {
      bestInstant = instant;
      bestId = id;
      best = name;
    }
  }
  return best;
}

const TrackingDrawerContent = styled(DrawerContent)`
  width: min(1240px, calc(100vw - 32px));

  @media (max-width: 700px) {
    width: 100vw;
  }
`;
const TrackingDrawerBody = styled(DrawerBody)`
  background: var(--MIST, #f7f8f9);
  padding: 24px 28px 40px;

  @media (max-width: 700px) {
    padding: 16px;
  }
`;
const DrawerEtaTag = styled.span`
  align-items: center;
  background: #eef4ff;
  border: 1px solid #c9d8f6;
  border-radius: 4px;
  color: var(--BLUE, #2060ea);
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 6px;
  line-height: 18px;
  padding: 2px 8px;
  white-space: nowrap;

  strong {
    color: var(--DARK-GREY, #0e0e2c);
    font-size: 12px;
  }
`;
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px 32px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;
const Field = styled.label`
  color: var(--DARK-GREY, #0e0e2c);
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  gap: 8px;
  letter-spacing: 0.025em;
  text-transform: uppercase;

  small {
    color: var(--ASH, #676879);
    font-weight: 400;
    letter-spacing: 0;
    line-height: 18px;
    margin-top: -4px;
    text-transform: none;
  }

  input {
    height: 40px;
    text-transform: none;
  }
`;
const FullField = styled(Field)`
  grid-column: 1 / -1;
`;
const FormSelect = styled.select`
  background: var(--CLOUD, #fcfdfe);
  border: 1px solid var(--LIGHT-ASH, #b9b9c6);
  border-radius: 4px;
  color: var(--ASH, #676879);
  font-size: 14px;
  height: 40px;
  padding: 4px 12px;
  text-transform: none;
`;
const FormTextArea = styled.textarea`
  background: var(--CLOUD, #fcfdfe);
  border: 1px solid var(--LIGHT-ASH, #b9b9c6);
  border-radius: 4px;
  color: var(--ASH, #676879);
  min-height: 88px;
  padding: 8px 12px;
  resize: vertical;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
`;
const CreationForm = styled.form`
  background: #fff;
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(14, 14, 44, 0.04);
  overflow: hidden;
`;
const FormHeader = styled.div`
  border-bottom: 1px solid var(--PEARL, #ecf1f4);
  padding: 24px 28px;

  h3 {
    color: var(--DARK-GREY, #0e0e2c);
    font-size: 20px;
    line-height: 26px;
    margin: 0;
  }

  p {
    color: var(--ASH, #676879);
    font-size: 14px;
    line-height: 21px;
    margin: 8px 0 0;
    max-width: 720px;
  }

  @media (max-width: 700px) {
    padding: 20px 16px;
  }
`;
const FormBody = styled.div`
  padding: 24px 28px 28px;

  @media (max-width: 700px) {
    padding: 20px 16px;
  }
`;
const FormFooter = styled.div`
  align-items: center;
  background: var(--MIST, #f7f8f9);
  border-top: 1px solid var(--PEARL, #ecf1f4);
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 28px;

  @media (max-width: 700px) {
    padding: 16px;
  }
`;
const Notice = styled.div`
  background: ${props =>
    props.$tone === "error"
      ? "var(--Red-0, #ffedef)"
      : props.$tone === "warning"
        ? "var(--Orange-0, #fcf2e6)"
        : "var(--MIST, #f7f8f9)"};
  border: 1px solid var(--PEARL, #ecf1f4);
  border-left: 3px solid
    ${props =>
      props.$tone === "error"
        ? "var(--Danger, #cf324a)"
        : props.$tone === "warning"
          ? "var(--Warning, #c97a20)"
          : "var(--BLUE, #2060ea)"};
  border-radius: 4px;
  color: var(--DARK-GREY, #0e0e2c);
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 16px;
  padding: 8px 12px;
`;
const Card = styled(DesignSystemCard)`
  margin-bottom: 20px;
  padding: 16px;
`;
const DetailsCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(14, 14, 44, 0.04);
  margin-bottom: 16px;
  overflow: hidden;
  padding: 0;
`;
const DetailsHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--PEARL, #ecf1f4);
  display: flex;
  gap: 20px;
  justify-content: space-between;
  padding: 16px 20px;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    padding: 18px 16px;
  }
`;
const HeaderIdentity = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-width: 0;
`;
const TagGroup = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: 700px) {
    justify-content: flex-start;
    width: 100%;
  }
`;
const TrackingReference = styled.h3`
  color: var(--DARK-GREY, #0e0e2c);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 26px;
  margin: 0;
`;
const TrackingMeta = styled.p`
  color: var(--ASH, #676879);
  flex-basis: 100%;
  font-size: 12px;
  line-height: 18px;
  margin: -2px 0 0;
`;
const SummaryItem = styled.div`
  color: var(--GREY, #4a4a68);
  font-size: 14px;
  line-height: 20px;

  strong {
    color: var(--DARK-GREY, #0e0e2c);
    display: block;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 2px;
  }
`;
const DetailsContent = styled.div`
  padding: 24px;

  @media (max-width: 700px) {
    padding: 16px;
  }
`;
const JourneyCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 8px 4px;
`;
const JourneyRoute = styled.div`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(120px, 1fr) minmax(100px, 0.7fr) minmax(120px, 1fr);

  @media (max-width: 560px) {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
`;
const RoutePoint = styled.div`
  min-width: 0;
  text-align: ${props => (props.$destination ? "right" : "left")};

  small {
    color: var(--ASH, #676879);
    display: block;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    margin-bottom: 6px;
    text-transform: uppercase;
  }

  strong {
    color: var(--DARK-GREY, #0e0e2c);
    display: block;
    font-size: 16px;
    line-height: 22px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 560px) {
    text-align: left;
  }
`;
const RouteLine = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;

  &::before,
  &::after {
    background: var(--LIGHT-ASH, #b9b9c6);
    content: "";
    flex: 1;
    height: 1px;
  }

  span {
    align-items: center;
    background: var(--CLOUD, #fcfdfe);
    border: 1px solid var(--PEARL, #ecf1f4);
    border-radius: 50%;
    color: var(--BLUE, #2060ea);
    display: flex;
    height: 32px;
    justify-content: center;
    width: 32px;
  }

  svg {
    height: 16px;
    width: 16px;
  }

  @media (max-width: 560px) {
    display: none;
  }
`;
const JourneyMeta = styled.div`
  border-top: 1px solid var(--PEARL, #ecf1f4);
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 18px;
  padding-top: 16px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;
const NextMilestone = styled.div`
  align-items: center;
  background: var(--CLOUD, #fcfdfe);
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 6px;
  color: var(--GREY, #4a4a68);
  display: flex;
  font-size: 13px;
  gap: 10px;
  margin-top: 16px;
  padding: 10px 12px;

  span {
    background: var(--BLUE, #2060ea);
    border-radius: 50%;
    box-shadow: 0 0 0 4px #e8efff;
    flex: 0 0 auto;
    height: 7px;
    width: 7px;
  }

  strong {
    color: var(--DARK-GREY, #0e0e2c);
  }
`;
const SyncDetails = styled.div`
  color: var(--ASH, #676879);
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 6px 18px;
  line-height: 18px;
  margin-top: 14px;
`;
const SupportingText = styled.p`
  color: var(--GREY, #4a4a68);
  font-size: 12px;
  line-height: 18px;
  margin: 8px 0 0;
`;
const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
  gap: 20px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;
const HistoryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  margin-top: 16px;
`;
const HistoryCard = styled(Card)`
  border-radius: 8px;
  margin-bottom: 0;
  min-width: 0;
  padding: 0;
  overflow: hidden;
`;
const EtaCard = styled(HistoryCard)`
  align-self: stretch;
  border-color: #c9d8f6;
`;
const HistoryHeader = styled.div`
  background: var(--MIST, #f7f8f9);
  border-bottom: 1px solid var(--PEARL, #ecf1f4);
  padding: 12px 16px;

  h4 {
    color: var(--GREY, #4a4a68);
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    margin: 0;
  }

  p {
    color: var(--ASH, #676879);
    font-size: 12px;
    line-height: 18px;
    margin: 4px 0 0;
  }

  > div {
    color: var(--GREY, #4a4a68);
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
    margin-top: 4px;
  }
`;
const EventList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 4px 16px 8px;
`;
const HistoryTitleRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
`;
const EventLegend = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const PanelContainer = styled.div`
  padding: 20px;
`;
const PanelHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;

  h2 {
    margin: 0;
  }
`;
const EventRow = styled.li`
  display: grid;
  grid-template-columns: 32px minmax(220px, 1.15fr) minmax(260px, 1fr) auto;
  gap: 16px;
  padding: 16px 0;
  position: relative;

  &:not(:last-child) {
    border-bottom: 1px solid var(--PEARL, #ecf1f4);
  }

  &:not(:last-child)::after {
    background: ${props => (props.$occurred ? "var(--Green-1, #8dd8bd)" : "var(--PEARL, #ecf1f4)")};
    content: "";
    height: calc(100% - 28px);
    left: 15px;
    position: absolute;
    top: 44px;
    width: 2px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 32px minmax(0, 1fr);
  }
`;
const EventMarker = styled.span`
  align-items: center;
  background: ${props => (props.$occurred ? "var(--Green-0, #e1fcef)" : "var(--MIST, #f7f8f9)")};
  border: 1px solid var(--PEARL, #ecf1f4);
  border-radius: 4px;
  color: ${props => (props.$occurred ? "var(--Green-2, #14804a)" : "var(--ASH, #676879)")};
  display: flex;
  height: 32px;
  justify-content: center;
  position: relative;
  width: 32px;
  z-index: 1;

  svg {
    height: 15px;
    width: 15px;
  }
`;
const EventContent = styled.div`
  min-width: 0;
`;
const EventCode = styled.div`
  color: var(--BLUE, #2060ea);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.055em;
  margin-bottom: 3px;
  text-transform: uppercase;
`;
const EventTitle = styled.strong`
  color: var(--DARK-GREY, #0e0e2c);
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const EventLocation = styled.div`
  align-self: center;
  color: var(--GREY, #4a4a68);
  font-size: 14px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  span {
    color: var(--ASH, #676879);
  }

  @media (max-width: 900px) {
    grid-column: 2;
  }
`;
const EventTransport = styled.div`
  color: var(--ASH, #676879);
  font-size: 12px;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const EventMeta = styled.div`
  align-items: center;
  align-self: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  min-width: 238px;
  text-align: right;

  time {
    color: var(--GREY, #4a4a68);
    font-size: 14px;
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    align-items: flex-start;
    grid-column: 2;
    justify-content: flex-start;
    min-width: 0;
    text-align: left;
  }
`;
const EtaList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 4px 20px 12px;

  li {
    border-bottom: 1px solid var(--PEARL, #ecf1f4);
    color: var(--GREY, #4a4a68);
    font-size: 14px;
    padding: 14px 0;
  }

  li:last-child {
    border-bottom: 0;
  }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const ProgressSpinner = styled.span`
  animation: ${spin} 0.7s linear infinite;
  border: 3px solid #d8e3fb;
  border-radius: 50%;
  border-top-color: var(--BLUE, #2060ea);
  display: inline-block;
  height: 30px;
  width: 30px;
`;
const ProgressPanel = styled(Card)`
  align-items: flex-start;
  display: flex;
  gap: 16px;
`;

const referenceLabels = {
  container: "Container number",
  booking_number: "Booking number",
  bill_of_lading: "Bill of lading number",
  air_waybill: "Air waybill number",
  shipment_reference: "Shipment reference",
  parcel_number: "Tracking number",
};

const isParcelSubscription = subscription =>
  subscription?.transport_mode === "parcel" && subscription?.tracking_type === "parcel_number";

export function TrackingForm({ shipment, shipmentLeg, consignment, onClose, onCreated }) {
  const [values, setValues] = useState({
    transport_mode: "ocean",
    tracking_type: "container",
    tracking_reference: "",
    delivery_postcode: "",
    integration: "",
    carrier_code: "",
  });
  const isParcel = values.transport_mode === "parcel" && values.tracking_type === "parcel_number";
  const {
    data: accounts = [],
    isLoading,
    error,
  } = useGetOceanTrackingIntegrationsQuery(values.tracking_type);
  const [create, { isLoading: saving }] = useCreateOceanTrackingMutation();
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (accounts.length !== 1) return;
    const onlyId = String(accounts[0].id);
    setValues(current =>
      current.integration === onlyId ? current : { ...current, integration: onlyId },
    );
  }, [accounts]);

  const change = event => {
    const next = { ...values, [event.target.name]: event.target.value };
    if (event.target.name === "transport_mode") {
      if (event.target.value === "air") next.tracking_type = "air_waybill";
      else if (event.target.value === "parcel") next.tracking_type = "parcel_number";
      else next.tracking_type = "container";
      next.integration = "";
    }
    if (event.target.name === "tracking_type") next.integration = "";
    setValues(next);
  };

  async function submit(event) {
    event.preventDefault();
    if (!values.integration) {
      setMessage("Select a tracking provider.");
      return;
    }
    if (
      values.tracking_type === "container" &&
      !validateOceanReference(values.tracking_reference)
    ) {
      setMessage("Enter a container number with four letters and seven digits (e.g. TCLU6223479).");
      return;
    }
    if (values.tracking_type === "booking_number" && !values.carrier_code.trim()) {
      setMessage("Carrier SCAC is required when tracking by booking number.");
      return;
    }
    try {
      const payload = {
        transport_mode: values.transport_mode,
        tracking_type: values.tracking_type,
        tracking_reference: isParcel
          ? values.tracking_reference.trim()
          : values.tracking_reference.trim().toUpperCase(),
        ...(isParcel
          ? {
              integration: Number(values.integration),
              delivery_postcode: values.delivery_postcode.trim().toUpperCase(),
            }
          : {
              integration: Number(values.integration),
              carrier_code: values.carrier_code.trim().toUpperCase(),
            }),
        ...(shipment ? { shipment: Number(shipment) } : {}),
        shipment_leg: shipmentLeg ? Number(shipmentLeg) : null,
        ...(consignment ? { consignment: Number(consignment) } : {}),
      };
      const result = await create(payload).unwrap();
      if (onCreated) onCreated(result);
      else onClose?.();
    } catch (createError) {
      setMessage(apiError(createError));
    }
  }

  return (
    <CreationForm aria-label="Track shipment" onSubmit={submit}>
      <FormHeader>
        <h3>Track shipment</h3>
        <p>
          Select how this shipment should be tracked and enter the reference supplied by the
          carrier.
        </p>
      </FormHeader>
      <FormBody>
        {(error || message) && <Notice $tone="error">{message || apiError(error)}</Notice>}
        {isLoading && <Notice>Loading tracking providers…</Notice>}
        {!isLoading && !error && !accounts.length && (
          <Notice>
            Ask an integration administrator to configure an active{" "}
            <Link to="/integration/findteu">tracking provider</Link>
            {isParcel ? " with Ship24 parcel tracking." : "."}
          </Notice>
        )}
        <FieldGrid>
          <Field>
            Shipment type
            <FormSelect name="transport_mode" value={values.transport_mode} onChange={change}>
              <option value="ocean">Ocean</option>
              <option value="air">Air</option>
              <option value="parcel">Parcel</option>
            </FormSelect>
          </Field>
          {!isParcel && (
            <Field>
              Tracking type
              <FormSelect name="tracking_type" value={values.tracking_type} onChange={change}>
                {values.transport_mode === "ocean" ? (
                  <>
                    <option value="container">Container</option>
                    <option value="booking_number">Booking number</option>
                    <option value="bill_of_lading">Bill of lading</option>
                    <option value="shipment_reference">Shipment reference</option>
                  </>
                ) : (
                  <>
                    <option value="air_waybill">Air waybill</option>
                    <option value="shipment_reference">Shipment reference</option>
                  </>
                )}
              </FormSelect>
            </Field>
          )}
          <Field>
            Tracking provider
            <FormSelect
              aria-label="Tracking provider"
              name="integration"
              value={values.integration}
              onChange={change}
              required
            >
              <option value="">Select provider</option>
              {accounts.map(account => (
                <option value={String(account.id)} key={account.id}>
                  {account.name}
                  {account.is_primary ? " (Primary)" : ""}
                </option>
              ))}
            </FormSelect>
          </Field>
          <Field>
            {referenceLabels[values.tracking_type]}
            <FormInput
              aria-label={referenceLabels[values.tracking_type]}
              name="tracking_reference"
              value={values.tracking_reference}
              onChange={change}
              placeholder={
                isParcel
                  ? "e.g. 15508440479380"
                  : `Enter ${referenceLabels[values.tracking_type].toLowerCase()}`
              }
              required
              maxLength={100}
            />
            <small>
              {isParcel
                ? "Enter the parcel number from your carrier confirmation."
                : values.tracking_type === "container"
                  ? "Four letters followed by seven digits."
                  : "Enter the reference supplied by the carrier."}
            </small>
          </Field>
          {isParcel && (
            <Field>
              Delivery postcode (optional)
              <FormInput
                aria-label="Delivery postcode"
                name="delivery_postcode"
                value={values.delivery_postcode}
                onChange={change}
                placeholder="e.g. B66 1BY"
                maxLength={16}
              />
              <small>
                Add the delivery postcode when the carrier needs it for an accurate ETA or location.
              </small>
            </Field>
          )}
          {!isParcel && (
            <Field>
              Carrier SCAC {values.tracking_type === "booking_number" ? "(required)" : "(optional)"}
              <FormInput
                aria-label="Carrier SCAC"
                name="carrier_code"
                value={values.carrier_code}
                onChange={change}
                placeholder="e.g. MAEU"
                required={values.tracking_type === "booking_number"}
                maxLength={4}
              />
              {values.tracking_type === "booking_number" && (
                <small>Required to identify the carrier for booking-number tracking.</small>
              )}
            </Field>
          )}
        </FieldGrid>
      </FormBody>
      <FormFooter>
        {onClose && (
          <ButtonT1 type="button" title="Cancel" $variant="secondary" onClickCallback={onClose} />
        )}
        <ButtonT1
          title="Activate tracking"
          loading={saving}
          disabled={!accounts.length || !values.integration}
        />
      </FormFooter>
    </CreationForm>
  );
}

function TrackingEditForm({ subscription, onCancel, onSaved }) {
  const [values, setValues] = useState({
    transport_mode: subscription.transport_mode || "ocean",
    tracking_type: subscription.tracking_type || "container",
    tracking_reference: subscription.tracking_reference || subscription.container_number || "",
    delivery_postcode: subscription.delivery_postcode || "",
    integration: String(subscription.integration || ""),
    carrier_code: subscription.carrier_code || "",
    reference: subscription.reference || "",
    notes: subscription.notes || "",
    original_eta: subscription.original_eta || "",
  });
  const isParcel = values.transport_mode === "parcel" && values.tracking_type === "parcel_number";
  const { data: providers = [], isLoading: loadingProviders } =
    useGetOceanTrackingIntegrationsQuery(values.tracking_type);
  const [update, { isLoading: saving }] = useUpdateOceanTrackingMutation();
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (providers.length !== 1) return;
    const onlyId = String(providers[0].id);
    setValues(current =>
      current.integration === onlyId ? current : { ...current, integration: onlyId },
    );
  }, [providers]);

  const change = event => {
    const next = { ...values, [event.target.name]: event.target.value };
    if (event.target.name === "transport_mode") {
      if (event.target.value === "air") next.tracking_type = "air_waybill";
      else if (event.target.value === "parcel") next.tracking_type = "parcel_number";
      else next.tracking_type = "container";
      next.integration = "";
    }
    if (event.target.name === "tracking_type") next.integration = "";
    setValues(next);
  };

  async function submit(event) {
    event.preventDefault();
    setMessage("");
    if (
      values.tracking_type === "container" &&
      !validateOceanReference(values.tracking_reference)
    ) {
      setMessage("Enter a container number with four letters and seven digits (e.g. TCLU6223479).");
      return;
    }
    const { delivery_postcode: deliveryPostcode, carrier_code: carrierCode, ...rest } = values;
    try {
      const result = await update({
        id: subscription.id,
        ...rest,
        integration: Number(values.integration),
        tracking_reference: isParcel
          ? values.tracking_reference.trim()
          : values.tracking_reference.trim().toUpperCase(),
        ...(isParcel
          ? { delivery_postcode: deliveryPostcode.trim().toUpperCase() }
          : { carrier_code: carrierCode.trim().toUpperCase() }),
        original_eta: values.original_eta || null,
      }).unwrap();
      onSaved(result);
    } catch (updateError) {
      setMessage(apiError(updateError));
    }
  }

  return (
    <form aria-label="Edit tracking details" onSubmit={submit}>
      <h3>Edit tracking details</h3>
      <SupportingText>
        Changing the tracking number, type, provider, or carrier restarts tracking with the
        corrected details. Reference, notes, and planned arrival can be updated without resetting
        milestones.
      </SupportingText>
      {message && <Notice $tone="error">{message}</Notice>}
      <FieldGrid>
        <Field>
          Shipment type
          <FormSelect name="transport_mode" value={values.transport_mode} onChange={change}>
            <option value="ocean">Ocean</option>
            <option value="air">Air</option>
            <option value="parcel">Parcel</option>
          </FormSelect>
        </Field>
        {!isParcel && (
          <Field>
            Tracking type
            <FormSelect name="tracking_type" value={values.tracking_type} onChange={change}>
              {values.transport_mode === "ocean" ? (
                <>
                  <option value="container">Container</option>
                  <option value="booking_number">Booking number</option>
                  <option value="bill_of_lading">Bill of lading</option>
                  <option value="shipment_reference">Shipment reference</option>
                </>
              ) : (
                <>
                  <option value="air_waybill">Air waybill</option>
                  <option value="shipment_reference">Shipment reference</option>
                </>
              )}
            </FormSelect>
          </Field>
        )}
        <Field>
          Tracking provider
          <FormSelect
            aria-label="Edit tracking provider"
            name="integration"
            value={values.integration}
            onChange={change}
          >
            <option value="">Select provider</option>
            {providers.map(provider => (
              <option key={provider.id} value={String(provider.id)}>
                {provider.name}
                {provider.is_primary ? " (Primary)" : ""}
              </option>
            ))}
          </FormSelect>
        </Field>
        <Field>
          {referenceLabels[values.tracking_type]}
          <FormInput
            aria-label={`Edit ${referenceLabels[values.tracking_type]}`}
            name="tracking_reference"
            value={values.tracking_reference}
            onChange={change}
            required
            maxLength={100}
          />
          <small>
            {isParcel
              ? "Enter the parcel number from your carrier confirmation."
              : values.tracking_type === "container"
                ? "Four letters followed by seven digits."
                : "Enter the reference supplied by the carrier."}
          </small>
        </Field>
        {isParcel ? (
          <Field>
            Delivery postcode (optional)
            <FormInput
              aria-label="Edit delivery postcode"
              name="delivery_postcode"
              value={values.delivery_postcode}
              onChange={change}
              placeholder="e.g. B66 1BY"
              maxLength={16}
            />
          </Field>
        ) : (
          <Field>
            Carrier SCAC (optional)
            <FormInput
              aria-label="Edit carrier SCAC"
              name="carrier_code"
              value={values.carrier_code}
              onChange={change}
              maxLength={4}
            />
          </Field>
        )}
        <Field>
          Original planned arrival (optional)
          <FormInput
            aria-label="Edit original planned arrival"
            type="date"
            name="original_eta"
            value={values.original_eta}
            onChange={change}
          />
        </Field>
        <FullField>
          Reference
          <FormInput
            aria-label="Edit reference"
            name="reference"
            value={values.reference}
            onChange={change}
            maxLength={200}
          />
        </FullField>
        <FullField>
          Notes
          <FormTextArea
            aria-label="Edit notes"
            name="notes"
            value={values.notes}
            onChange={change}
            maxLength={4000}
          />
        </FullField>
      </FieldGrid>
      <Actions>
        <ButtonT1
          title="Save changes"
          loading={saving}
          disabled={loadingProviders || !values.integration}
        />
        <ButtonT1 type="button" title="Cancel" $variant="secondary" onClickCallback={onCancel} />
      </Actions>
    </form>
  );
}

function eventOccurred(category) {
  return ["actual", "occurred", "completed"].includes(String(category || "").toLowerCase());
}

function statusLabel(value) {
  return label(value).replace(/\b\w/g, character => character.toUpperCase());
}

function EventIcon({ event }) {
  const value = `${event.code || ""} ${event.description || ""}`.toLowerCase();
  if (/gate|deliver|truck/.test(value)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M3 6h11v10H3zM14 10h4l3 3v3h-7z" />
        <circle cx="7" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
      </svg>
    );
  }
  if (/load|discharg|depart|arriv|vessel|ship/.test(value)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M5 11h14l-2 7H7zM9 11V6h6v5M12 6V3" />
        <path d="M3 21c2-1 3-1 5 0 2-1 3-1 5 0 2-1 3-1 5 0" />
      </svg>
    );
  }
  if (/empty|return|container/.test(value)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="6" width="18" height="12" rx="1" />
        <path d="M7 8v8M11 8v8M15 8v8M19 8v8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

/**
 * Paginated ETA or scan list for one Track subscription.
 *
 * @param {{
 *   id: number,
 *   kind: string,
 *   enabled: boolean,
 *   emptyLabel?: string
 * }} props
 * @return {*}
 */
function History({ id, kind, enabled, emptyLabel }) {
  const timeZone = usePlaceTimezone();
  const [offset, setOffset] = useState(0);
  const { data, isLoading, error } = useGetOceanTrackingHistoryQuery(
    { id, kind, offset },
    { skip: !enabled },
  );
  if (isLoading) return <p>Loading…</p>;
  if (error) return <Notice $tone="error">{apiError(error)}</Notice>;
  const items = data?.results || [];
  return (
    <>
      {!items.length && (
        <p>
          {emptyLabel ||
            (kind === "events"
              ? "FindTEU did not send milestones yet."
              : "FindTEU did not send a tracker ETA yet.")}
        </p>
      )}
      {kind === "events" ? (
        <EventList aria-label="Shipment events">
          {items.map(item => {
            const occurred = eventOccurred(item.category);
            const location = [item.location?.name, item.location?.country]
              .filter(Boolean)
              .join(", ");
            const transport = [item.transport?.vessel, item.transport?.voyage]
              .filter(Boolean)
              .join(" · ");
            return (
              <EventRow key={item.id} $occurred={occurred}>
                <EventMarker $occurred={occurred} aria-hidden="true">
                  <EventIcon event={item} />
                </EventMarker>
                <EventContent>
                  <EventCode>{label(item.code || item.category)}</EventCode>
                  <EventTitle>{item.description || label(item.code)}</EventTitle>
                </EventContent>
                <EventLocation>
                  {location || "Location unavailable"}
                  {item.location?.terminal && <span> · {item.location.terminal}</span>}
                  {transport && <EventTransport>Vessel / voyage: {transport}</EventTransport>}
                </EventLocation>
                <EventMeta>
                  <time dateTime={item.transport?.occurred_at || item.event_date || undefined}>
                    {trackWhen(item.transport?.occurred_at || item.event_date, timeZone)}
                  </time>
                  <StatusTag className={occurred ? "Completed" : "Pending"}>
                    {occurred ? "✓ Occurred" : "◷ Upcoming"}
                  </StatusTag>
                </EventMeta>
              </EventRow>
            );
          })}
        </EventList>
      ) : (
        <EtaList>
          {items.map(item => (
            <li key={item.id}>
              <strong>
                {item.previous_eta ? `${trackWhen(item.previous_eta, timeZone)} → ` : ""}
                {trackWhen(item.eta, timeZone)}
              </strong>
              <div>Recorded {reportedTime(item.received_at, timeZone)}</div>
            </li>
          ))}
        </EtaList>
      )}
      {data?.count > 50 && (
        <Actions>
          <ButtonT1
            title="Previous"
            $variant="secondary"
            disabled={!offset}
            onClickCallback={() => setOffset(Math.max(0, offset - 50))}
          />
          <ButtonT1
            title="Next"
            $variant="secondary"
            disabled={offset + 50 >= data.count}
            onClickCallback={() => setOffset(offset + 50)}
          />
        </Actions>
      )}
    </>
  );
}

function TrackingDetails({ subscription, drawerOpen, taskState, onTaskStarted }) {
  const timeZone = usePlaceTimezone();
  const [action, { isLoading }] = useOceanTrackingActionMutation();
  const [message, setMessage] = useState("");
  const [confirmStop, setConfirmStop] = useState(false);
  const [editing, setEditing] = useState(false);
  const { startMonitoring } = useTaskMonitor();
  const snapshot = subscription.snapshot || {};
  const parcel = isParcelSubscription(subscription);
  const { data: eventHistory } = useGetOceanTrackingHistoryQuery(
    { id: subscription.id, kind: "events", offset: 0 },
    { skip: !drawerOpen },
  );
  const eventRows = eventHistory?.results || [];
  const eventCount = Number(eventHistory?.count) || 0;
  const eventsCoverJourney = eventCount > 0 && eventCount <= eventRows.length;
  const currentPlace = parcel
    ? reportedOr(
        (eventsCoverJourney ? latestOccurredLocation(eventRows) : "") ||
          snapshot.current_location?.name,
        "No location from courier",
      )
    : snapshot.current_location?.name;
  const terminal = ["completed", "stopped"].includes(subscription.state);
  const syncStamp = `${subscription.last_successful_sync_at || ""}:${
    subscription.provider_updated_at || ""
  }`;
  const syncStampRef = useRef(syncStamp);

  useEffect(() => {
    if (syncStampRef.current === syncStamp) return;
    syncStampRef.current = syncStamp;
    setMessage("");
  }, [syncStamp]);

  useEffect(() => {
    if (!SUCCESSFUL_TASK_STATUSES.has(taskState?.status)) return;
    setMessage("");
  }, [taskState?.status]);

  async function run(operation) {
    setMessage("");
    try {
      const result = await action({
        id: subscription.id,
        action: operation,
      }).unwrap();
      if (result.task_id) {
        startMonitoring(result.task_id);
        onTaskStarted?.(result.task_id);
      }
      setMessage(result.detail);
      setConfirmStop(false);
    } catch (actionError) {
      setMessage(apiError(actionError));
    }
  }

  function saved(result) {
    if (result.task_id) {
      startMonitoring(result.task_id);
      onTaskStarted?.(result.task_id);
    }
    setMessage(
      result.task_id
        ? "Tracking details updated. A fresh provider synchronization has started."
        : "Tracking details updated.",
    );
    setEditing(false);
  }

  if (editing) {
    return (
      <Card>
        <TrackingEditForm
          subscription={subscription}
          onCancel={() => setEditing(false)}
          onSaved={saved}
        />
      </Card>
    );
  }

  return (
    <DetailsCard>
      <DetailsHeader>
        <HeaderIdentity>
          <TrackingReference>
            {subscription.tracking_reference || subscription.container_number}
          </TrackingReference>
          <TagGroup>
            <StatusTag className={statusLabel(subscription.state)}>
              {statusLabel(subscription.state)}
            </StatusTag>
            <StatusTag className={statusLabel(subscription.current_status)}>
              {statusLabel(subscription.current_status)}
            </StatusTag>
          </TagGroup>
          <TrackingMeta>
            {parcel
              ? `${subscription.integration_name} · Parcel${snapshot.carrier ? ` · ${snapshot.carrier}` : ""}`
              : `${subscription.integration_name} · ${label(subscription.tracking_type || "container")} · ${label(subscription.transport_mode || "ocean")}`}
          </TrackingMeta>
        </HeaderIdentity>
        {subscription.can_manage && (
          <HeaderActions>
            <ButtonT1
              title="Edit details"
              aria-label="Edit tracking details"
              $variant="secondary"
              onClickCallback={() => setEditing(true)}
            />
            {!terminal && (
              <>
                <ButtonT1
                  title={subscription.state === "failed" ? "Retry tracking" : "Request refresh"}
                  loading={isLoading}
                  disabled={!subscription.integration_active}
                  onClickCallback={() => run(subscription.state === "failed" ? "retry" : "refresh")}
                />
                <ButtonT1
                  title="Stop tracking"
                  $variant="danger"
                  disabled={isLoading}
                  onClickCallback={() => setConfirmStop(true)}
                />
              </>
            )}
          </HeaderActions>
        )}
      </DetailsHeader>
      <DetailsContent>
        {!subscription.integration_active && (
          <Notice>Tracking updates are paused because the integration is inactive.</Notice>
        )}
        {subscription.is_stale && (
          <Notice $tone="warning">
            The latest available tracking information is shown while an update is pending.
          </Notice>
        )}
        {subscription.last_error && <Notice $tone="error">{subscription.last_error}</Notice>}
        <OverviewGrid>
          <JourneyCard>
            {parcel ? (
              <>
                <JourneyMeta>
                  <SummaryItem>
                    <strong>Origin</strong>
                    <div>{reportedOr(snapshot.origin?.name, "No origin from courier")}</div>
                  </SummaryItem>
                  <SummaryItem>
                    <strong id="track-current-location">Current location</strong>
                    <div aria-labelledby="track-current-location">{currentPlace}</div>
                  </SummaryItem>
                  <SummaryItem>
                    <strong>Status</strong>
                    <div>{snapshot.status_label || snapshot.next_milestone || "Unavailable"}</div>
                  </SummaryItem>
                  <SummaryItem>
                    <strong>Latest ETA</strong>
                    <div>
                      {subscription.latest_eta
                        ? trackWhen(subscription.latest_eta, timeZone)
                        : "Not available yet"}
                    </div>
                  </SummaryItem>
                  <SummaryItem>
                    <strong>Carrier</strong>
                    <div>{snapshot.carrier || "Not reported yet"}</div>
                  </SummaryItem>
                </JourneyMeta>
              </>
            ) : (
              <>
                <JourneyRoute>
                  <RoutePoint>
                    <small>Origin</small>
                    <strong>{snapshot.origin?.name || "Unavailable"}</strong>
                  </RoutePoint>
                  <RouteLine aria-hidden="true">
                    <span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M5 11h14l-2 7H7zM9 11V6h6v5M12 6V3" />
                        <path d="M3 21c2-1 3-1 5 0 2-1 3-1 5 0 2-1 3-1 5 0" />
                      </svg>
                    </span>
                  </RouteLine>
                  <RoutePoint $destination>
                    <small>Destination</small>
                    <strong>{snapshot.destination?.name || "Unavailable"}</strong>
                  </RoutePoint>
                </JourneyRoute>
                <JourneyMeta>
                  <SummaryItem>
                    <strong>Carrier</strong>
                    <div>{snapshot.carrier || subscription.carrier_code || "Unavailable"}</div>
                  </SummaryItem>
                  <SummaryItem>
                    <strong>Vessel / voyage</strong>
                    <div>
                      {[snapshot.transport?.vessel, snapshot.transport?.voyage]
                        .filter(Boolean)
                        .join(" · ") || "Unavailable"}
                    </div>
                  </SummaryItem>
                </JourneyMeta>
                <NextMilestone>
                  <span aria-hidden="true" />
                  <div>
                    Next expected milestone:{" "}
                    <strong>{snapshot.next_milestone || "Unavailable"}</strong>
                  </div>
                </NextMilestone>
              </>
            )}
            <SyncDetails>
              <span>
                Provider update: {reportedTime(subscription.provider_updated_at, timeZone)}
              </span>
              <span>
                Last synchronized: {reportedTime(subscription.last_successful_sync_at, timeZone)}
              </span>
            </SyncDetails>
            {subscription.notes && <SupportingText>{subscription.notes}</SupportingText>}
          </JourneyCard>
          {(subscription.last_successful_sync_at || subscription.state === "completed") && (
            <EtaCard>
              <HistoryHeader>
                <h4>ETA history</h4>
                <p>
                  Original{" "}
                  {subscription.original_eta
                    ? trackWhen(subscription.original_eta, timeZone)
                    : "not entered"}
                </p>
                {subscription.eta_variance_days != null && (
                  <div>
                    {subscription.eta_variance_days > 0
                      ? `${subscription.eta_variance_days} days later`
                      : subscription.eta_variance_days < 0
                        ? `${Math.abs(subscription.eta_variance_days)} days earlier`
                        : "On original plan"}
                  </div>
                )}
              </HistoryHeader>
              <History
                id={subscription.id}
                kind="eta_history"
                enabled={drawerOpen}
                emptyLabel={
                  parcel
                    ? "The courier did not send an arrival date yet."
                    : "FindTEU did not send a tracker ETA yet."
                }
              />
            </EtaCard>
          )}
        </OverviewGrid>
        {message && <Notice>{message}</Notice>}
        {confirmStop && (
          <Notice $tone="warning">
            <p>Stop future updates for this journey? Its tracking history will remain available.</p>
            <Actions>
              <ButtonT1
                title="Confirm stop"
                $variant="danger"
                onClickCallback={() => run("stop")}
              />
              <ButtonT1
                title="Cancel"
                $variant="secondary"
                onClickCallback={() => setConfirmStop(false)}
              />
            </Actions>
          </Notice>
        )}
        {(subscription.last_successful_sync_at || subscription.state === "completed") && (
          <HistoryGrid>
            <HistoryCard>
              <HistoryHeader>
                <HistoryTitleRow>
                  <div>
                    <h4>Detailed events</h4>
                    <p>Carrier milestones in chronological order</p>
                  </div>
                  <EventLegend aria-label="Event status legend">
                    <StatusTag className="Completed">Occurred</StatusTag>
                    <StatusTag className="Pending">Upcoming</StatusTag>
                  </EventLegend>
                </HistoryTitleRow>
              </HistoryHeader>
              <History
                id={subscription.id}
                kind="events"
                enabled={drawerOpen}
                emptyLabel={
                  parcel
                    ? "The courier did not send scans yet."
                    : "FindTEU did not send milestones yet."
                }
              />
            </HistoryCard>
          </HistoryGrid>
        )}
      </DetailsContent>
    </DetailsCard>
  );
}

function TrackingProgress({ subscription, taskState, onTaskStarted }) {
  const [editing, setEditing] = useState(false);
  const { startMonitoring } = useTaskMonitor();
  const waitingForProvider =
    subscription.current_status === "awaiting_provider_update" ||
    (taskState?.status === "PROCESSING" && Number(taskState?.progress) >= 65);
  const stepText =
    taskState?.step ||
    (waitingForProvider
      ? "Tracking is registered with the provider. Waiting for the next update…"
      : "Waiting for the first update from the tracking provider…");
  if (editing) {
    return (
      <Card>
        <TrackingEditForm
          subscription={subscription}
          onCancel={() => setEditing(false)}
          onSaved={result => {
            if (result.task_id) {
              if (onTaskStarted) onTaskStarted(result.task_id);
              else startMonitoring(result.task_id);
            }
            setEditing(false);
          }}
        />
      </Card>
    );
  }
  return (
    <ProgressPanel>
      <ProgressSpinner aria-hidden="true" />
      <div>
        <h3>{waitingForProvider ? "Waiting for provider update" : "Tracking in progress"}</h3>
        <p>{stepText}</p>
        <strong>{subscription.tracking_reference || subscription.container_number}</strong>
        <p>
          {waitingForProvider
            ? "This is normal while the provider prepares the first update. Tracking continues in the background even if you close this view."
            : "You can close this drawer. Tracking will continue in the background."}
        </p>
        {subscription.last_error && <Notice $tone="warning">{subscription.last_error}</Notice>}
        {subscription.can_manage && (
          <ButtonT1
            title="Edit tracking details"
            $variant="secondary"
            onClickCallback={() => setEditing(true)}
          />
        )}
      </div>
    </ProgressPanel>
  );
}

function TrackingDrawer({
  isOpen,
  onClose,
  consignment,
  shipmentNumber,
  data,
  isLoading,
  error,
  taskState,
  onCreated,
  onTaskStarted,
}) {
  const timeZone = usePlaceTimezone();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animateOpen, setAnimateOpen] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const timer = setTimeout(() => setAnimateOpen(true), 10);
      return () => clearTimeout(timer);
    }
    setAnimateOpen(false);
    const timer = setTimeout(() => setShouldRender(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = event => event.key === "Escape" && onClose();
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isOpen, onClose]);
  const drawerRoot = document.getElementById("root-modals");
  if (!shouldRender || !drawerRoot) return null;
  const subscriptions = data?.results || [];
  const latestEta = subscriptions.find(subscription => subscription.latest_eta)?.latest_eta;
  return createPortal(
    <DrawerOverlay $isOpen={animateOpen} onClick={onClose}>
      <TrackingDrawerContent
        role="dialog"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
      >
        <PortalHeader
          title={shipmentNumber || `Shipment ${consignment}`}
          supplementary={
            latestEta ? (
              <DrawerEtaTag>
                Latest ETA <strong>{trackWhen(latestEta, timeZone)}</strong>
              </DrawerEtaTag>
            ) : null
          }
          onClose={onClose}
        />
        <TrackingDrawerBody>
          {isLoading && <Notice>Loading shipment tracking…</Notice>}
          {error && <Notice $tone="error">{apiError(error)}</Notice>}
          {!isLoading && !error && !subscriptions.length && (
            <TrackingForm consignment={consignment} onClose={onClose} onCreated={onCreated} />
          )}
          {subscriptions.map(subscription =>
            subscription.state === "pending" && !subscription.last_successful_sync_at ? (
              <TrackingProgress
                key={subscription.id}
                subscription={subscription}
                taskState={taskState}
                onTaskStarted={onTaskStarted}
              />
            ) : (
              <TrackingDetails
                key={subscription.id}
                subscription={subscription}
                drawerOpen={isOpen}
                taskState={taskState}
                onTaskStarted={onTaskStarted}
              />
            ),
          )}
        </TrackingDrawerBody>
      </TrackingDrawerContent>
    </DrawerOverlay>,
    drawerRoot,
  );
}

export function ConsignmentTrackingControl({
  consignment,
  shipmentNumber,
  deferQuery = false,
  initialHasTracking = false,
  renderTrigger,
  onTrackingUpdated,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [optimisticSubscription, setOptimisticSubscription] = useState(null);
  const completedTaskRef = useRef(null);
  const updateSignatureRef = useRef(null);
  const notify = useNotification();
  const { startMonitoring } = useTaskMonitor();
  const tasks = useSelector(state => state.notification?.tasks || {});
  const superuser = useSelector(state => state.auth.user?.is_superuser);
  const hasPermission = usePermission();
  const canRead = superuser || hasPermission("view_consignment");
  const canAdd = superuser || hasPermission("change_consignment");
  // On Kanban cards, skip the query until the drawer opens. `initialHasTracking`
  // only drives the button label so a closed board does not poll once per card.
  const pageVisible = usePageVisible();
  const [inFlight, setInFlight] = useState(false);
  const { data, isLoading, error, refetch } = useGetOceanTrackingQuery(
    { consignment, offset: 0 },
    {
      skip: !consignment || !canRead || (deferQuery && !isOpen),
      pollingInterval: trackingPollInterval(pageVisible, inFlight),
    },
  );
  const subscriptions = useMemo(() => {
    const byId = new Map();
    if (optimisticSubscription) byId.set(optimisticSubscription.id, optimisticSubscription);
    (data?.results || []).forEach(item => byId.set(item.id, item));
    return [...byId.values()];
  }, [data, optimisticSubscription]);
  const pending = subscriptions.find(
    item => item.state === "pending" && !item.last_successful_sync_at,
  );
  const failed = subscriptions.find(
    item => item.state === "failed" && !item.last_successful_sync_at,
  );
  const taskId = activeTaskId || pending?.task_id || failed?.task_id;
  const taskState = taskId ? tasks[taskId] : undefined;
  const waitingForFirstSync = Boolean(pending);
  const workInFlight = trackingWorkInFlight({
    waitingForFirstSync,
    startedTaskId: activeTaskId,
    taskStatus: taskState?.status,
  });
  const updateSignature = subscriptions
    .map(
      item =>
        `${item.id}:${item.latest_eta || ""}:${item.last_successful_sync_at || ""}:${item.provider_updated_at || ""}`,
    )
    .sort()
    .join("|");
  const trackingLabel = () => {
    const containerRef =
      subscriptions.find(item => item.tracking_reference || item.container_number)
        ?.tracking_reference ||
      subscriptions.find(item => item.container_number)?.container_number ||
      "container";
    const shipmentLabel = shipmentNumber || "shipment";
    return { shipmentLabel, containerRef };
  };
  const notifyTrackingUpdated = () => {
    const { shipmentLabel, containerRef } = trackingLabel();
    notify({
      type: "success",
      message: `Tracking updated for ${shipmentLabel} (${containerRef}).`,
    });
  };
  useEffect(() => setInFlight(workInFlight), [workInFlight]);
  useEffect(() => {
    if (taskId && !tasks[taskId]) startMonitoring(taskId);
  }, [startMonitoring, taskId, tasks]);
  useEffect(() => {
    const status = taskState?.status;
    if (!TERMINAL_TASK_STATUSES.has(status) || completedTaskRef.current === taskId) return;
    completedTaskRef.current = taskId;
    refetch?.();
    if (SUCCESSFUL_TASK_STATUSES.has(status)) onTrackingUpdated?.();
    if (activeTaskId === taskId) setActiveTaskId(null);
  }, [activeTaskId, onTrackingUpdated, refetch, taskId, taskState?.status]);
  useEffect(() => {
    if (updateSignatureRef.current === null) {
      updateSignatureRef.current = updateSignature;
      return;
    }
    if (updateSignatureRef.current === updateSignature) return;
    updateSignatureRef.current = updateSignature;
    const meaningful = subscriptions.some(item => item.latest_eta || item.last_successful_sync_at);
    if (!meaningful) return;
    notifyTrackingUpdated();
    onTrackingUpdated?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSignature, onTrackingUpdated, subscriptions]);
  const hasTracking = subscriptions.length > 0 || (deferQuery && !isOpen && initialHasTracking);
  const buttonLabel = isLoading
    ? "Loading Tracking"
    : pending
      ? "Tracking in Progress"
      : failed
        ? "Tracking Failed"
        : hasTracking
          ? "View Tracking Details"
          : "Track";
  const created = result => {
    setOptimisticSubscription(result);
    setInFlight(true);
    if (result.task_id) {
      setActiveTaskId(result.task_id);
      startMonitoring(result.task_id);
    }
    refetch?.();
  };
  if (!canRead) return null;
  const drawerData = data
    ? { ...data, results: subscriptions }
    : { count: subscriptions.length, results: subscriptions };
  const triggerDisabled = isLoading || (!hasTracking && !canAdd);
  const openDrawer = event => {
    event?.stopPropagation?.();
    if (!triggerDisabled) setIsOpen(true);
  };
  const triggerProps = {
    disabled: triggerDisabled,
    hasTracking,
    label: buttonLabel,
    onClick: openDrawer,
  };
  return (
    <>
      {renderTrigger ? (
        renderTrigger(triggerProps)
      ) : (
        <ButtonT1
          className="headerItem"
          title={buttonLabel}
          $variant="primaryLight"
          disabled={triggerDisabled}
          onClickCallback={openDrawer}
        />
      )}
      <TrackingDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        consignment={consignment}
        shipmentNumber={shipmentNumber}
        data={drawerData}
        isLoading={isLoading}
        error={error}
        taskState={taskState}
        onCreated={created}
        onTaskStarted={nextTaskId => {
          setActiveTaskId(nextTaskId);
          setInFlight(true);
          startMonitoring(nextTaskId);
        }}
      />
    </>
  );
}

function TrackingPanel({
  shipment,
  shipmentLeg,
  consignment,
  oceanLeg = true,
  hideAddAction = false,
}) {
  const [adding, setAdding] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [inFlight, setInFlight] = useState(false);
  const tasks = useSelector(state => state.notification?.tasks || {});
  const { startMonitoring } = useTaskMonitor();
  const superuser = useSelector(state => state.auth.user?.is_superuser);
  const hasPermission = usePermission();
  const forLeg = Boolean(shipmentLeg);
  const forConsignment = Boolean(consignment);
  const canRead =
    superuser ||
    (forConsignment
      ? hasPermission("view_consignment")
      : hasPermission("view_shipment") && (!forLeg || hasPermission("view_shipmentleg")));
  const canAdd =
    superuser ||
    (forConsignment
      ? hasPermission("change_consignment")
      : hasPermission("change_shipment") && (!forLeg || hasPermission("change_shipmentleg")));
  const pageVisible = usePageVisible();
  const { data, isLoading, error } = useGetOceanTrackingQuery(
    { shipment, shipmentLeg, consignment, offset: 0 },
    {
      skip: (!shipment && !consignment) || !oceanLeg || !canRead,
      pollingInterval: trackingPollInterval(pageVisible, inFlight),
    },
  );
  const subscriptions = useMemo(() => data?.results || [], [data]);
  const pendingSubscription = subscriptions.find(
    item => item.state === "pending" && !item.last_successful_sync_at,
  );
  const taskId = activeTaskId || pendingSubscription?.task_id;
  const taskState = taskId ? tasks[taskId] : undefined;
  const workInFlight = trackingWorkInFlight({
    waitingForFirstSync: Boolean(pendingSubscription),
    startedTaskId: activeTaskId,
    taskStatus: taskState?.status,
  });
  useEffect(() => setInFlight(workInFlight), [workInFlight]);
  useEffect(() => {
    if (taskId && !tasks[taskId]) startMonitoring(taskId);
  }, [startMonitoring, taskId, tasks]);
  const startTask = nextTaskId => {
    setActiveTaskId(nextTaskId);
    setInFlight(true);
    startMonitoring(nextTaskId);
  };
  useEffect(() => {
    if (activeTaskId && TERMINAL_TASK_STATUSES.has(taskState?.status)) {
      setActiveTaskId(null);
    }
  }, [activeTaskId, taskState?.status]);
  if (!canRead) return <Notice>Shipment tracking access is required.</Notice>;
  if (!oceanLeg) return <Notice>Tracking is currently available for ocean legs only.</Notice>;
  return (
    <PanelContainer>
      <PanelHeader>
        <h2>{consignment ? "Consignment tracking" : "Shipment tracking"}</h2>
        {canAdd && !hideAddAction && (
          <ButtonT1
            title="Add tracking"
            onClickCallback={() => setAdding(true)}
            data-guide="tracking.first_shipment"
          />
        )}
      </PanelHeader>
      {adding && (
        <TrackingForm
          shipment={shipment}
          shipmentLeg={shipmentLeg}
          consignment={consignment}
          onClose={() => setAdding(false)}
        />
      )}
      {isLoading && <Notice>Loading shipment tracking…</Notice>}
      {error && <Notice $tone="error">{apiError(error)}</Notice>}
      {!isLoading && !error && !subscriptions.length && (
        <Notice>No tracking subscriptions yet.</Notice>
      )}
      {subscriptions.map(subscription =>
        subscription.state === "pending" && !subscription.last_successful_sync_at ? (
          <TrackingProgress
            key={subscription.id}
            subscription={subscription}
            taskState={taskId === subscription.task_id ? taskState : undefined}
            onTaskStarted={startTask}
          />
        ) : (
          <TrackingDetails
            key={subscription.id}
            subscription={subscription}
            drawerOpen
            taskState={taskState}
            onTaskStarted={startTask}
          />
        ),
      )}
    </PanelContainer>
  );
}

export function OceanTrackingPanel({ forLeg = false }) {
  const { master, paramId } = useDetailPageContext();
  const shipment = forLeg ? master?.shipment?.id : paramId;
  const shipmentLeg = forLeg ? paramId : undefined;
  const mode = master?.transport_mode?.id || master?.transport_mode;
  return (
    <TrackingPanel
      shipment={shipment}
      shipmentLeg={shipmentLeg}
      oceanLeg={!forLeg || mode === "SEA"}
    />
  );
}

export function ConsignmentTrackingPanel({ consignment }) {
  return <TrackingPanel consignment={consignment} hideAddAction />;
}
